/**
 * 常用句收藏
 * ============================================================
 * 场景：旅行时随时翻出存好的句子用。
 *
 * ── 离线能力的真实边界（界面上如实区分）────────────────────
 *   · 文字（方言原句/注音/回译）→ localStorage → **离线可用**
 *   · 音频 → 首次播放后缓存到 IndexedDB → **可离线播放**
 *   · 从未播放过的音频 → **需要联网**才能合成
 *   所以每条都标「可离线」或「需联网」，不让用户到没网时才发现放不出来。
 *
 * ── 播放策略 ────────────────────────────────────────────────
 *   先查本地缓存 → 命中直接播（不请求网络）
 *   未命中 → 请求 TTS，**顺手存入缓存**，下次就能离线播
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import {
  getSavedPhrases,
  removePhrase,
  clearSavedPhrases,
  getAudio,
  putAudio,
  getCachedIds,
  estimateAudioBytes,
} from '../lib/savedPhrases';
import BottomNav from '../components/BottomNav';

export default function SavedPhrasesPage() {
  /*
   * ⚠️ 收藏列表**同步**读取，不放在 useEffect 里。
   *    localStorage 是同步的，放 effect 会导致首屏先渲染空态再跳出内容
   *    （用户看到闪一下「还没有收藏」）。对本地数据没有理由异步。
   *    IndexedDB 的音频缓存才是异步的，那部分留在 effect 里。
   */
  const [items, setItems] = useState(() => getSavedPhrases());
  const [cachedIds, setCachedIds] = useState(() => new Set());
  const [bytes, setBytes] = useState(0);

  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const refresh = useCallback(async () => {
    const list = getSavedPhrases();
    setItems(list);
    const [ids, b] = await Promise.all([
      getCachedIds(list.map((x) => x.id)),
      estimateAudioBytes(),
    ]);
    setCachedIds(ids);
    setBytes(b);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 卸载时释放音频
  useEffect(
    () => () => {
      try {
        audioRef.current?.pause();
      } catch {
        /* 忽略 */
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  const playBlob = (blob) =>
    new Promise((resolve, reject) => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      if (!audioRef.current) audioRef.current = new Audio();
      const a = audioRef.current;
      a.src = url;
      a.onended = () => setPlayingId(null);
      a.onerror = () => {
        setPlayingId(null);
        reject(new Error('音频播放失败'));
      };
      a.play().then(resolve).catch(reject);
    });

  /** 播放：本地缓存优先，未命中才联网 */
  const play = useCallback(async (item) => {
    if (playingId === item.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    setError(null);
    setPlayingId(item.id);

    try {
      // ① 先查缓存
      let blob = await getAudio(item.id);

      // ② 未命中 → 联网合成，并**顺手缓存**
      if (!blob) {
        setLoadingId(item.id);
        const resp = await api.post(
          '/api/tts',
          { text: item.dialectText, language: 'cantonese', dialect: item.dialectId },
          { responseType: 'blob' }
        );
        blob = resp.data;
        setLoadingId(null);
        const ok = await putAudio(item.id, blob);
        if (ok) setCachedIds((prev) => new Set(prev).add(item.id));
      }

      await playBlob(blob);
    } catch (e) {
      setLoadingId(null);
      setPlayingId(null);
      setError(
        navigator.onLine
          ? e.message || '播放失败'
          : '这句还没有离线缓存，需要联网播放一次后即可离线使用'
      );
    }
  }, [playingId]);

  const remove = (id) => {
    removePhrase(id);
    setCachedIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    refresh();
  };

  const doClear = () => {
    clearSavedPhrases();
    setConfirmClear(false);
    refresh();
  };

  const kb = (bytes / 1024).toFixed(0);

  return (
    <div className="min-h-screen bg-[#fdfaf1] pb-28 dark:bg-zinc-900">
      <header className="bg-gradient-to-br from-teal-500 to-sky-500 px-5 pb-6 pt-12 dark:from-teal-700 dark:to-sky-700">
        <div className="flex items-center gap-3">
          <Link to="/translate" className="text-white/90 active:opacity-60" aria-label="返回翻译">
            <span className="material-icons">arrow_back</span>
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">常用句收藏</h1>
            <p className="mt-0.5 text-xs text-white/75">
              {items.length} 句 · 离线音频 {kb} KB
            </p>
          </div>
        </div>
      </header>

      <main className="space-y-3 px-5 py-5">
        {/* 离线说明 —— 不让用户到没网时才发现放不出来 */}
        <section className="rounded-2xl bg-white p-3.5 dark:bg-zinc-800">
          <p className="text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
            <span className="font-bold">文字随时可看</span>（存在本机）。
            音频**听过一次就会缓存**，之后没网也能播。
            标「需联网」的句子还没缓存过音频。
          </p>
        </section>

        {error && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {error}
          </p>
        )}

        {/* 空态 */}
        {items.length === 0 && (
          <div className="rounded-2xl bg-white p-8 text-center dark:bg-zinc-800">
            <span className="material-icons text-4xl text-zinc-300">bookmark_border</span>
            <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">还没有收藏的句子</p>
            <p className="mt-1 text-[11px] text-zinc-400">
              在翻译页翻好一句后，点右上角的收藏按钮存下来
            </p>
            <Link
              to="/translate"
              className="mt-4 inline-block rounded-full bg-teal-500 px-4 py-2 text-xs font-bold text-white"
            >
              去翻译
            </Link>
          </div>
        )}

        {/* 列表 */}
        {items.map((it) => {
          const offline = cachedIds.has(it.id);
          const isPlaying = playingId === it.id;
          const isLoading = loadingId === it.id;
          return (
            <section key={it.id} className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                      {it.dialectName || it.dialectId}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        offline
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400'
                      }`}
                    >
                      {offline ? '可离线' : '需联网'}
                    </span>
                    {it.uncertain && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        未经确认
                      </span>
                    )}
                  </div>

                  <p className="text-base font-bold leading-snug text-zinc-800 dark:text-zinc-100">
                    {it.dialectText}
                  </p>
                  {it.romanization && (
                    <p className="mt-0.5 font-mono text-[11px] text-teal-600 dark:text-teal-400">
                      {it.romanization}
                    </p>
                  )}
                  {it.backTranslation && (
                    <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                      {it.backTranslation}
                    </p>
                  )}
                  {it.source && it.source !== it.backTranslation && (
                    <p className="mt-1 text-[10px] text-zinc-400">原文：{it.source}</p>
                  )}
                </div>

                {/* 操作 */}
                <div className="flex shrink-0 flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => play(it)}
                    disabled={isLoading}
                    className={`flex h-10 w-10 items-center justify-center rounded-full text-white active:scale-95 disabled:opacity-50 ${
                      isPlaying ? 'bg-rose-500' : 'bg-teal-500'
                    }`}
                    aria-label={isPlaying ? '暂停' : '播放'}
                  >
                    <span className="material-icons !text-xl">
                      {isLoading ? 'hourglass_empty' : isPlaying ? 'pause' : 'play_arrow'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    className="text-zinc-300 active:opacity-60 dark:text-zinc-600"
                    aria-label="取消收藏"
                  >
                    <span className="material-icons !text-lg">bookmark</span>
                  </button>
                </div>
              </div>
            </section>
          );
        })}

        {/* 清空 */}
        {items.length > 0 && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            {!confirmClear ? (
              <button
                type="button"
                onClick={() => setConfirmClear(true)}
                className="w-full rounded-xl border border-rose-200 py-2.5 text-xs font-bold text-rose-600 dark:border-rose-800 dark:text-rose-400"
              >
                清空全部收藏
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={doClear}
                  className="flex-1 rounded-xl bg-rose-500 py-2.5 text-xs font-bold text-white"
                >
                  确认清空（含离线音频）
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmClear(false)}
                  className="flex-1 rounded-xl bg-zinc-100 py-2.5 text-xs font-bold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
                >
                  取消
                </button>
              </div>
            )}
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
